import { MATRIX_LABELS, type UnfInvoiceCandidateDto, type UnfInvoiceNewLine } from '@crm/shared'

/**
 * Как записать строку счёта, которой нет в заявке (21.10.2026): «та же позиция, что №…»
 * (в заявке партномер записан иначе) или новая позиция с квотой менеджера — матрица,
 * поставщик и закупочная цена. Менеджер их знает: он уже вписал товар в УНФ.
 */
export type LineChoice =
  | { mode: 'link'; itemId: string }
  | {
      mode: 'new'
      matrix: string
      supplierName: string
      costPrice: string
      costCurrency: UnfInvoiceNewLine['costCurrency']
    }

export const EMPTY_NEW: LineChoice = {
  mode: 'new',
  matrix: '',
  supplierName: '',
  costPrice: '',
  costCurrency: 'RUB',
}

const num = (v: string): number => Number(v.replace(/\s/g, '').replace(',', '.'))

/** Квота новой строки заполнена: матрица, поставщик, закупочная цена больше нуля. */
export function newLineSpec(no: number, c: LineChoice | undefined): UnfInvoiceNewLine | null {
  if (!c || c.mode !== 'new') return null
  const cost = num(c.costPrice)
  if (!c.matrix || !c.supplierName.trim() || !(cost > 0)) return null
  return {
    no,
    matrix: c.matrix as UnfInvoiceNewLine['matrix'],
    supplierName: c.supplierName.trim(),
    costPrice: cost,
    costCurrency: c.costCurrency,
  }
}

const input =
  'w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none'

export function UnfInvoiceLineChoice({
  choice,
  linkable,
  current,
  disabled,
  onChange,
}: {
  choice: LineChoice | undefined
  linkable: UnfInvoiceCandidateDto['linkable']
  /** Позиция, с которой строка уже связана (её нет в linkable). */
  current: { itemId: string; partNumber: string } | null
  disabled: boolean
  onChange: (c: LineChoice) => void
}) {
  // Строка уже связана (менеджером или догадкой «похоже») — по умолчанию выбрана эта позиция.
  const value: LineChoice =
    choice ?? (current ? { mode: 'link', itemId: current.itemId } : EMPTY_NEW)
  const options = [
    ...(current ? [{ itemId: current.itemId, label: current.partNumber }] : []),
    ...linkable.map((l) => ({
      itemId: l.itemId,
      // Без номера: «№» в заказе — номер строки счёта 1С, у позиции вне счёта его нет.
      label: `${l.partNumber} · ${l.quantity.toLocaleString('ru-RU')} шт`,
    })),
  ]
  return (
    <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-2">
      <select
        aria-label="Как записать строку"
        className={input}
        disabled={disabled}
        value={value.mode === 'link' ? value.itemId : ''}
        onChange={(e) =>
          onChange(e.target.value ? { mode: 'link', itemId: e.target.value } : EMPTY_NEW)
        }
      >
        <option value="">Новая позиция — внести квоту</option>
        {options.map((o) => (
          <option key={o.itemId} value={o.itemId}>
            Та же позиция заказа, что {o.label}
          </option>
        ))}
      </select>
      {value.mode === 'new' && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[8rem_1fr_7rem_5rem]">
          <select
            aria-label="Матрица"
            className={input}
            disabled={disabled}
            value={value.matrix}
            onChange={(e) => onChange({ ...value, matrix: e.target.value })}
          >
            <option value="">Матрица…</option>
            {MATRIX_LABELS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <input
            aria-label="Поставщик"
            className={input}
            placeholder="Поставщик"
            disabled={disabled}
            value={value.supplierName}
            onChange={(e) => onChange({ ...value, supplierName: e.target.value })}
          />
          <input
            aria-label="Закупочная цена"
            className={input}
            placeholder="Закуп. цена"
            inputMode="decimal"
            disabled={disabled}
            value={value.costPrice}
            onChange={(e) => onChange({ ...value, costPrice: e.target.value })}
          />
          <select
            aria-label="Валюта"
            className={input}
            disabled={disabled}
            value={value.costCurrency}
            onChange={(e) =>
              onChange({
                ...value,
                costCurrency: e.target.value as UnfInvoiceNewLine['costCurrency'],
              })
            }
          >
            {(['RUB', 'USD', 'EUR', 'CNY'] as const).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  )
}
